import { useState } from 'react';
import { Search, Edit2, QrCode, XCircle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@foundation/altzero_base/base/components/ui/badge';
import { Button } from '@foundation/altzero_base/base/components/ui/button';
import { Input } from '@foundation/altzero_base/base/components/ui/input';
import type { Venue } from '../types/venue.types';
import { theme } from '../../../theme';

type VenueManagementProps = {
  venues: Venue[];
  onEdit: (venue: Venue) => void;
  onQRCode: (venue: Venue) => void;
  onToggleActive: (venue: Venue) => void;
};

export const VenueManagement = ({
  venues,
  onEdit,
  onQRCode,
  onToggleActive,
}: VenueManagementProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = venues.filter((v) => {
    const q = searchQuery.toLowerCase();
    return (
      v.venue_name.toLowerCase().includes(q) ||
      v.venue_slug.toLowerCase().includes(q) ||
      (v.sport?.toLowerCase().includes(q) ?? false)
    );
  });

  return (
    <div>
      {/* Search */}
      <div className="relative mb-4">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.text.muted}`} />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search venues..."
          className="pl-9"
        />
      </div>

      {/* Table */}
      <div className={`overflow-x-auto rounded-lg border ${theme.border.base}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${theme.border.base} dark:bg-white/5 bg-gray-50`}>
              <th className={`text-left px-4 py-3 font-medium ${theme.text.body}`}>Name</th>
              <th className={`text-left px-4 py-3 font-medium ${theme.text.body}`}>Slug</th>
              <th className={`text-left px-4 py-3 font-medium ${theme.text.body}`}>Sport</th>
              <th className={`text-left px-4 py-3 font-medium ${theme.text.body}`}>Status</th>
              <th className={`text-right px-4 py-3 font-medium ${theme.text.body}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className={`px-4 py-8 text-center ${theme.text.muted}`}>
                  {searchQuery ? 'No venues match your search' : 'No venues yet. Create your first venue.'}
                </td>
              </tr>
            ) : (
              filtered.map((venue) => (
                <tr key={venue.id} className={theme.card.row}>
                  <td className={`px-4 py-3 font-medium ${theme.text.heading}`}>
                    {venue.venue_name}
                  </td>
                  <td className={`px-4 py-3 ${theme.text.body} font-mono text-xs`}>
                    /qr/{venue.venue_slug}
                  </td>
                  <td className={`px-4 py-3 ${theme.text.body}`}>
                    {venue.sport ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={venue.is_active ? 'default' : 'secondary'}>
                      {venue.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(venue)}
                        title="Edit venue"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onQRCode(venue)}
                        title="QR Code"
                      >
                        <QrCode className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggleActive(venue)}
                        title={venue.is_active ? 'Deactivate' : 'Reactivate'}
                      >
                        {venue.is_active ? (
                          <XCircle className="w-4 h-4 text-red-400" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-green-400" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
